import React, { useEffect, useState } from "react";
import "../../App.css";
import "../redesign.css";
import { Outlet, useLocation } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { routeDescription, routeTitles } from "../../metadata/MetaData";
import RouteChangeTracker from "../../analytics/RouteChangeTracker";
import { migrateLegacyCookies } from "../../ReusableFunctions/playbackPosition";
import PlayerProvider from "../player/PlayerProvider";
import MiniPlayer from "../player/MiniPlayer";
import ExpandedSheet from "../player/ExpandedSheet";
import { ADMIN_BASE } from "./paths";

/*
 * Root layout for the whole app — the data-router element that never unmounts
 * across navigations. Owns what used to live in App.js (meta tags, GA tracker,
 * legacy-cookie migration, scroll restoration) plus the persistent playback
 * layer: PlayerProvider's media element survives every route change, which is
 * what keeps audio playing while the user browses.
 */

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function RootShell() {
  const [MetaData, setMetaData] = useState({
    title: "",
    description: "",
    url: "window.location.origin",
  });

  // Listeners who used the site before positions moved to localStorage are
  // carrying one cookie per class played. Enough of them overflows the request
  // headers and the origin drops the connection, so clear them out on load.
  useEffect(() => {
    migrateLegacyCookies();
  }, []);

  const location = useLocation();
  useEffect(() => {
    const currentRoute = location.pathname;
    const url = window.location.origin + currentRoute;
    const title =
      routeTitles[currentRoute] ||
      "Inside Chassidus | Audio Classes | Chassidus for Everyone";
    const description =
      routeDescription[currentRoute] ||
      "Welcome to Inside Chassidus where you can learn at your own pace with world renowned teacher and inspirational speaker Rabbi Yossi Paltiel.";

    setMetaData((prevData) => ({
      ...prevData,
      title,
      description,
      url,
    }));
  }, [location]);

  const isAdmin = location.pathname.startsWith(ADMIN_BASE);

  return (
    <HelmetProvider className="App">
      <Helmet>
        <title>{`${MetaData.title}`}</title>
        <meta name="description" content={MetaData.description} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website/article"></meta>
        <meta property="og:title" content={MetaData.title}></meta>
        <meta property="og:description" content={MetaData.description}></meta>
        <meta property="og:url" content={MetaData.url}></meta>
        <meta property="og:site_name" content="Inside Chassidus" />

        <meta
          property="og:image"
          itemprop="image"
          content="https://media.insidechassidus.org/wp-content/uploads/20201027210730/r-paltiel-inside-chassidus-new.png"
        />
        <meta property="og:image:type" content="image/jpeg/png" />

        <meta property="og:image:width" content="300" />
        <meta property="og:image:height" content="200" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={MetaData.title} />
        <meta property="twitter:description" content={MetaData.description} />
        <meta
          property="twitter:image"
          itemprop="image"
          content="https://media.insidechassidus.org/wp-content/uploads/20201027210730/r-paltiel-inside-chassidus-new.png"
        />
        <meta property="twitter:image:width" content="300" />
        <meta property="twitter:image:height" content="200" />
      </Helmet>
      <ScrollToTop />
      <RouteChangeTracker />
      <PlayerProvider>
        <Outlet />
        {/* Persistent mini-player: public surfaces only, never over the admin */}
        {!isAdmin && <MiniPlayer />}
        {!isAdmin && <ExpandedSheet />}
      </PlayerProvider>
    </HelmetProvider>
  );
}
